import {
  Box,
  Button,
  ClickAwayListener,
  FormControlLabel,
  Paper,
  Popper,
  Radio,
  ScopedCssBaseline,
  Stack,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { endOfToday, startOfToday, subDays } from 'date-fns';
import { KeyboardEvent, useState } from 'react';

import { drawerZIndex } from '../../components/filter/FilterDrawer';
import { FilterOption, predefinedOptions } from './hooks/useBeginFilterParams';

const datePickerSlotProps = {
  textField: {
    sx: {
      '& .MuiOutlinedInput-root': {
        '& input': { height: '0.5rem' },
      },
    },
  },
  popper: {
    sx: {
      zIndex: (theme: Theme) => drawerZIndex(theme) + 2,
    },
  },
};

export const BeginFilterPopper = ({
  anchorEl,
  setOpen,
  initialStartDate,
  initialEndDate,
  option,
  setOption,
  setDates,
}: {
  anchorEl: HTMLDivElement | null;
  setOpen: (open: boolean) => void;
  initialStartDate: Date | null;
  initialEndDate: Date | null;
  option: FilterOption | null;
  setOption: (option: FilterOption) => void;
  setDates: (startDate: Date | null, endDate: Date | null) => void;
}) => {
  const [localStartDate, setLocalStartDate] = useState(initialStartDate);
  const [localEndDate, setLocalEndDate] = useState(initialEndDate);
  const theme = useTheme();

  const resetDates = (): void => {
    setOption('today');
    setOpen(false);
  };

  const applyChanges = () => {
    if (option === 'custom') {
      setDates(localStartDate, localEndDate);
    }
    setOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyChanges();
    }
  };

  function renderOption(predefinedOption: (typeof predefinedOptions)[number]) {
    const formControl = (
      <FormControlLabel
        key={predefinedOption.value}
        control={<Radio checked={option === predefinedOption.value} />}
        label={predefinedOption.label}
        onClick={() => {
          setOption(predefinedOption.value);
          if (predefinedOption.value !== 'custom') {
            setOpen(false);
          }
        }}
      />
    );

    if (predefinedOption.value !== 'custom') {
      return formControl;
    }

    return (
      <Stack gap={1} key={predefinedOption.value} onClick={() => setOption('custom')}>
        {formControl}
        <Box display="flex" gap={1} maxWidth="24rem">
          <DatePicker
            label="von"
            defaultValue={localStartDate ?? startOfToday()}
            onChange={(date, context) => {
              if (context.validationError) {
                return;
              }
              setLocalStartDate(date ?? startOfToday());
            }}
            minDate={subDays(startOfToday(), 7)}
            slotProps={datePickerSlotProps}
          />
          <DatePicker
            label="bis"
            defaultValue={localEndDate}
            onChange={(date, context) => {
              if (context.validationError) {
                return;
              }
              setLocalEndDate(date);
            }}
            minDate={localStartDate ?? endOfToday()}
            slotProps={datePickerSlotProps}
          />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Button
            variant="text"
            size="small"
            sx={{ color: theme.palette.text.primary }}
            onClick={(event) => {
              event.stopPropagation();
              resetDates();
            }}
          >
            Zurücksetzen
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              applyChanges();
            }}
          >
            Übernehmen
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Popper open anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: drawerZIndex(theme) + 1 }}>
      <ScopedCssBaseline>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper sx={{ p: 2, display: 'grid' }} onKeyDown={handleKeyDown}>
            <Typography variant="label" color="textTertiary" mb={1}>
              Veranstaltungsbeginn
            </Typography>
            {predefinedOptions.map(renderOption)}
          </Paper>
        </ClickAwayListener>
      </ScopedCssBaseline>
    </Popper>
  );
};
